import { useTranslation } from 'react-i18next';
import { Box, type BoxProps } from '@chakra-ui/react';

interface LoadingIndicatorProps extends BoxProps {
  size?: 'small' | 'medium' | 'default';
  active?: boolean;
}

/**
 * Box-type loading indicator with 4 animated colored boxes
 * Used consistently across all screens for initial widget display
 */
export const LoadingIndicator = ({
  size = 'default',
  active = true,
  ...props
}: LoadingIndicatorProps) => {
  const { t } = useTranslation();

  // Size-specific values
  const getSizes = () => {
    if (size === 'small') {
      return { container: { w: 4, h: 4 }, box: { w: '3px', h: '3px', mr: '1px' }, gap: '1px' };
    }
    if (size === 'medium') {
      return { container: { w: 8, h: 8 }, box: { w: '35px', h: '35px', mr: '3px' }, gap: '3px' };
    }
    return { container: {}, box: { w: 1.5, h: 1.5, mr: 0.5 }, gap: 0.5 };
  };

  const sizes = getSizes();

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      {...sizes.container}
      role="status"
      aria-label={t('app.loading')}
      {...props}
    >
      <Box position="relative">
        <Box display="flex" mb={sizes.gap}>
          <Box
            {...sizes.box}
            animation={active ? 'rotateBox1' : 'rotateBoxGray'}
            bg={active ? 'loading.box1' : 'gray.500'}
          />
          <Box
            {...sizes.box}
            animation={active ? 'rotateBox2' : 'rotateBoxGray'}
            animationDelay="0.5s"
            bg={active ? 'loading.box2' : 'gray.500'}
          />
        </Box>
        <Box display="flex" mb={sizes.gap}>
          <Box
            {...sizes.box}
            animation={active ? 'rotateBox3' : 'rotateBoxGray'}
            animationDelay="1.5s"
            bg={active ? 'loading.box3' : 'gray.500'}
          />
          <Box
            {...sizes.box}
            animation={active ? 'rotateBox4' : 'rotateBoxGray'}
            animationDelay="1s"
            bg={active ? 'loading.box4' : 'gray.500'}
          />
        </Box>
      </Box>
    </Box>
  );
};
